/**
 * /auth/callback — landing page after a Supabase OAuth redirect
 * (Google Sign-In). Supabase parses the URL hash inside its
 * `detectSessionInUrl` flow, so by the time React mounts there's
 * either a session or an error in `URLSearchParams`.
 *
 * Flow:
 *
 *   1. The auth store's `bootstrapAuth()` subscription fires when
 *      Supabase commits the new session, populating `useAuthStore`.
 *   2. We poll the store for a non-null user (with a 10-second
 *      ceiling) and then redirect to whatever the login page stashed
 *      in `sessionStorage['gi-engine.post-login-redirect']` (or `/`).
 *   3. If `error` / `error_description` are in the URL, we surface
 *      them and offer a link back to /login instead of redirecting.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@/stores/authStore';

const REDIRECT_KEY = 'gi-engine.post-login-redirect';
const POLL_INTERVAL_MS = 200;
const POLL_TIMEOUT_MS = 10_000;

export function AuthCallbackPage() {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [timedOut, setTimedOut] = useState(false);

  // Surface OAuth-side errors directly. Supabase puts them in the
  // query string OR the URL hash depending on the provider; we read
  // both.
  const oauthError = useMemo(() => {
    const fromQuery = params.get('error_description') ?? params.get('error');
    if (fromQuery) return fromQuery;
    if (typeof window !== 'undefined' && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      return hashParams.get('error_description') ?? hashParams.get('error');
    }
    return null;
  }, [params]);

  useEffect(() => {
    if (oauthError) return;
    let elapsed = 0;
    const timer = setInterval(() => {
      const user = useAuthStore.getState().user;
      if (user) {
        clearInterval(timer);
        const target = sessionStorage.getItem(REDIRECT_KEY) ?? '/';
        sessionStorage.removeItem(REDIRECT_KEY);
        navigate(target, { replace: true });
        return;
      }
      elapsed += POLL_INTERVAL_MS;
      if (elapsed >= POLL_TIMEOUT_MS) {
        clearInterval(timer);
        setTimedOut(true);
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [navigate, oauthError]);

  if (oauthError) {
    return (
      <div className="space-y-3 text-center">
        <h2 className="text-lg font-semibold tracking-tight">
          {t('callback.error_heading', { defaultValue: 'Sign-in failed' })}
        </h2>
        <p className="text-sm text-ink-muted">{oauthError}</p>
        <Link to="/login" className="text-sm font-medium text-spark hover:underline">
          {t('callback.back_to_login', { defaultValue: 'Back to login' })}
        </Link>
      </div>
    );
  }

  if (timedOut) {
    return (
      <div className="space-y-3 text-center">
        <h2 className="text-lg font-semibold tracking-tight">
          {t('callback.timeout_heading', { defaultValue: 'Still working on it…' })}
        </h2>
        <p className="text-sm text-ink-muted">
          {t('callback.timeout_body', {
            defaultValue:
              'Sign-in is taking longer than expected. Try refreshing, or head back to the login page.',
          })}
        </p>
        <Link to="/login" className="text-sm font-medium text-spark hover:underline">
          {t('callback.back_to_login', { defaultValue: 'Back to login' })}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-2 text-center">
      <h2 className="text-lg font-semibold tracking-tight">
        {t('callback.heading', { defaultValue: 'Finishing sign-in…' })}
      </h2>
      <p className="text-sm text-ink-muted">
        {t('callback.body', {
          defaultValue:
            "Hold tight — we're verifying your session. You'll be redirected automatically.",
        })}
      </p>
    </div>
  );
}
